import { assertMover, isPassable, passableNeighbours } from './clearance.js';
import { VEC2_ZERO, directionTo, distance, vec2 } from './geometry.js';
import { cellAt, cellIndex } from './grid.js';
const createFrontier = () => {
    const cells = [];
    const priorities = [];
    const orders = [];
    let inserted = 0;
    // `noUncheckedIndexedAccess` types every read as optional; the three parallel
    // arrays are written in lockstep, so these fallbacks are unreachable.
    const cellOf = (slot) => cells[slot] ?? -1;
    const priorityOf = (slot) => priorities[slot] ?? Number.POSITIVE_INFINITY;
    const orderOf = (slot) => orders[slot] ?? 0;
    const isBefore = (a, b) => {
        const byPriority = priorityOf(a) - priorityOf(b);
        return byPriority === 0 ? orderOf(a) < orderOf(b) : byPriority < 0;
    };
    const swap = (a, b) => {
        [cells[a], cells[b]] = [cellOf(b), cellOf(a)];
        [priorities[a], priorities[b]] = [priorityOf(b), priorityOf(a)];
        [orders[a], orders[b]] = [orderOf(b), orderOf(a)];
    };
    const siftUp = (from) => {
        let child = from;
        while (child > 0) {
            const parent = (child - 1) >> 1;
            if (!isBefore(child, parent))
                return;
            swap(child, parent);
            child = parent;
        }
    };
    const siftDown = (from) => {
        let parent = from;
        for (;;) {
            const left = parent * 2 + 1;
            const right = left + 1;
            let best = parent;
            if (left < cells.length && isBefore(left, best))
                best = left;
            if (right < cells.length && isBefore(right, best))
                best = right;
            if (best === parent)
                return;
            swap(parent, best);
            parent = best;
        }
    };
    return {
        push: (cell, priority) => {
            cells.push(cell);
            priorities.push(priority);
            orders.push(inserted);
            inserted += 1;
            siftUp(cells.length - 1);
        },
        pop: () => {
            const top = cellOf(0);
            const last = cells.length - 1;
            swap(0, last);
            cells.pop();
            priorities.pop();
            orders.pop();
            siftDown(0);
            return top;
        },
    };
};
/**
 * The cost of the cheapest unobstructed route between two cells: straight steps
 * cost 1, diagonals √2.
 *
 * This is A*'s heuristic. It never overestimates — no route can beat it on an
 * empty map — which is exactly what keeps the search optimal.
 */
export const octileDistance = (a, b) => {
    const dx = Math.abs(b.x - a.x);
    const dy = Math.abs(b.y - a.y);
    return Math.abs(dx - dy) + Math.SQRT2 * Math.min(dx, dy);
};
const reconstruct = (grid, cameFrom, goal) => {
    const reversed = [];
    for (let index = goal; index >= 0; index = cameFrom[index] ?? -1) {
        reversed.push(cellAt(grid, index));
    }
    return reversed.reverse();
};
/**
 * The cheapest route from `from` to `to`, or `null` when there is none.
 *
 * Continuous positions are welcome: each end resolves to the cell containing
 * it. `null` means genuinely unreachable — a solid endpoint, an endpoint off
 * the map, a target walled off from the start, or a route too narrow for the
 * mover — and is a normal answer, not an error: an entity ordered somewhere it
 * cannot go simply does not go.
 */
export const findPath = (grid, from, to, mover) => {
    assertMover(grid, mover);
    const start = cellIndex(grid, from);
    const goal = cellIndex(grid, to);
    if (start < 0 || goal < 0 || !isPassable(grid, from, mover) || !isPassable(grid, to, mover))
        return null;
    if (start === goal)
        return [cellAt(grid, start)];
    const goalCell = cellAt(grid, goal);
    const size = grid.width * grid.height;
    const costs = new Array(size).fill(Number.POSITIVE_INFINITY);
    const cameFrom = new Array(size).fill(-1);
    const settled = new Array(size).fill(false);
    const frontier = createFrontier();
    costs[start] = 0;
    frontier.push(start, octileDistance(cellAt(grid, start), goalCell));
    for (let current = frontier.pop(); current >= 0; current = frontier.pop()) {
        if (current === goal)
            return reconstruct(grid, cameFrom, goal);
        // The heap holds stale duplicates rather than paying for a decrease-key;
        // the first time a cell surfaces is its final cost, the rest are noise.
        if (settled[current] === true)
            continue;
        settled[current] = true;
        const cell = cellAt(grid, current);
        const costHere = costs[current] ?? Number.POSITIVE_INFINITY;
        for (const neighbour of passableNeighbours(grid, cell, mover)) {
            const index = cellIndex(grid, neighbour);
            const candidate = costHere + distance(cell, neighbour);
            if (candidate >= (costs[index] ?? Number.POSITIVE_INFINITY))
                continue;
            costs[index] = candidate;
            cameFrom[index] = current;
            frontier.push(index, candidate + octileDistance(neighbour, goalCell));
        }
    }
    return null;
};
/**
 * `true` when an entity can walk the straight line between two cells without
 * touching rock.
 *
 * Traced from the lower cell index to the higher whichever way it was asked, so
 * the answer cannot depend on the direction of the question: string pulling
 * would otherwise smooth a corner from one side that it refuses from the other.
 * Diagonal crossings demand both flanking cells, matching the movement rule.
 *
 * With a mover, every cell the line crosses must have room for the whole disc,
 * not merely for its centre — so a gap a point sees straight through is opaque
 * to anything too wide to pass it, and a smoothed line keeps its distance from
 * the wall instead of hugging it.
 */
export const hasLineOfSight = (grid, from, to, mover) => {
    assertMover(grid, mover);
    const fromIndex = cellIndex(grid, from);
    const toIndex = cellIndex(grid, to);
    if (fromIndex < 0 || toIndex < 0 || !isPassable(grid, from, mover) || !isPassable(grid, to, mover)) {
        return false;
    }
    const origin = cellAt(grid, Math.min(fromIndex, toIndex));
    const destination = cellAt(grid, Math.max(fromIndex, toIndex));
    let { x, y } = origin;
    const spanX = Math.abs(destination.x - x);
    const spanY = Math.abs(destination.y - y);
    const stepX = destination.x > x ? 1 : -1;
    const stepY = destination.y > y ? 1 : -1;
    let error = spanX - spanY;
    while (x !== destination.x || y !== destination.y) {
        const doubled = 2 * error;
        const movesX = doubled > -spanY;
        const movesY = doubled < spanX;
        if (movesX &&
            movesY &&
            !(isPassable(grid, vec2(x + stepX, y), mover) && isPassable(grid, vec2(x, y + stepY), mover))) {
            return false;
        }
        if (movesX) {
            error -= spanY;
            x += stepX;
        }
        if (movesY) {
            error += spanX;
            y += stepY;
        }
        if (!isPassable(grid, vec2(x, y), mover))
            return false;
    }
    return true;
};
/**
 * String pulling: drops every waypoint the entity can simply walk past.
 *
 * A* hands back a tile-by-tile staircase because that is how the grid is
 * shaped, not how anything moves. Walking the raw path looks robotic; walking
 * the smoothed one is the smooth diagonal the design calls for. The result is
 * never longer than the input — a straight line between two points that can see
 * each other cannot beat the detour it replaces.
 *
 * Smooth a disc's route with the same mover that found it. Pulled straight as a
 * point, a route that legitimately clears a corner by a body's width collapses
 * onto a line that grazes it, and the entity walks through the rock it had just
 * been routed around.
 */
export const smoothPath = (grid, path, mover) => {
    const first = path[0];
    // Nothing to pull straight: two waypoints are already a straight line, and
    // fewer than that is not a route at all.
    if (first === undefined || path.length < 3)
        return path;
    const smoothed = [first];
    let anchor = first;
    let previous = first;
    for (const cell of path.slice(1)) {
        if (!hasLineOfSight(grid, anchor, cell, mover)) {
            // The line of sight broke somewhere between `previous` and `cell`, so
            // `previous` is a corner the entity has to actually turn around.
            smoothed.push(previous);
            anchor = previous;
        }
        previous = cell;
    }
    smoothed.push(previous);
    return smoothed;
};
/**
 * Solves the whole map towards one target in a single Dijkstra sweep.
 *
 * This is what a pack chases: forty skeletons sampling one field cost the same
 * as one, they never queue behind each other's searches, and because every cell
 * stores the step its own shortest route takes, following the field is exactly
 * as optimal as running A* per skeleton would have been.
 *
 * One field serves one body size. A pack of discs of the same radius shares a
 * field; a wider monster needs its own, because the cells it may occupy are a
 * different set. Cells too tight for the mover are simply never expanded, so
 * they keep the `Infinity` and the zero step that mean "no route from here".
 */
export const createFlowField = (grid, target, mover) => {
    assertMover(grid, mover);
    const targetIndex = cellIndex(grid, target);
    if (targetIndex < 0 || !isPassable(grid, target, mover)) {
        throw new RangeError(`Flow field target (${target.x}, ${target.y}) is not a cell an entity of this size can stand on in this ${grid.width}×${grid.height} map`);
    }
    const size = grid.width * grid.height;
    const distances = new Array(size).fill(Number.POSITIVE_INFINITY);
    const directions = new Array(size).fill(VEC2_ZERO);
    const settled = new Array(size).fill(false);
    const frontier = createFrontier();
    distances[targetIndex] = 0;
    frontier.push(targetIndex, 0);
    for (let current = frontier.pop(); current >= 0; current = frontier.pop()) {
        if (settled[current] === true)
            continue;
        settled[current] = true;
        const cell = cellAt(grid, current);
        const costHere = distances[current] ?? Number.POSITIVE_INFINITY;
        for (const neighbour of passableNeighbours(grid, cell, mover)) {
            const index = cellIndex(grid, neighbour);
            const candidate = costHere + distance(cell, neighbour);
            if (candidate >= (distances[index] ?? Number.POSITIVE_INFINITY))
                continue;
            distances[index] = candidate;
            // The cell we came from *is* the next step towards the target.
            directions[index] = directionTo(neighbour, cell);
            frontier.push(index, candidate);
        }
    }
    return { grid, target: cellAt(grid, targetIndex), distances, directions };
};
/** The unit step towards the target from wherever the entity is standing; zero when there is nowhere to go. */
export const flowAt = (field, position) => {
    const index = cellIndex(field.grid, position);
    return index < 0 ? VEC2_ZERO : (field.directions[index] ?? VEC2_ZERO);
};
/** Cost of the trip to the target from here, or `Infinity` when it cannot be made. */
export const flowDistanceAt = (field, position) => {
    const index = cellIndex(field.grid, position);
    return index < 0 ? Number.POSITIVE_INFINITY : (field.distances[index] ?? Number.POSITIVE_INFINITY);
};
//# sourceMappingURL=pathfinding.js.map